import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { format } from "date-fns";
import toast from "react-hot-toast";
import { b2bAPI } from "../services/api";
import {
  RefreshCw,
  Eye,
  EyeOff,
  Copy,
  RotateCcw,
  Trash2,
  Webhook,
  Activity,
  LogOut,
  ExternalLink,
} from "lucide-react";

const STATUS_COLOR = {
  active: { bg: "rgba(34,197,94,0.1)", text: "var(--green)" },
  pending: { bg: "rgba(245,166,35,0.1)", text: "var(--accent)" },
  suspended: { bg: "rgba(239,68,68,0.1)", text: "var(--red)" },
};

// ── Login screen ──────────────────────────────────────────────────────────────
function DevLogin({ onLogin }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState(
    localStorage.getItem("kq_b2b_email") || "",
  );
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!email || !password) {
      toast.error("Enter your email and password");
      return;
    }
    setLoading(true);
    try {
      const r = await b2bAPI.login({ email, password });
      const data = r.data.data;
      localStorage.setItem("kq_b2b_key", data.api_key);
      localStorage.setItem("kq_b2b_secret", data.api_secret);
      localStorage.setItem("kq_b2b_email", email);
      onLogin(data);
      toast.success(`Welcome back, ${data.name}!`);
    } catch (e) {
      toast.error(e.response?.data?.message || "Login failed");
    }
    setLoading(false);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "100px 6% 60px",
      }}
    >
      <div
        style={{
          position: "fixed",
          top: "30%",
          left: "50%",
          transform: "translateX(-50%)",
          width: 600,
          height: 400,
          background:
            "radial-gradient(ellipse,rgba(245,166,35,0.07) 0%,transparent 70%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          animation: "fadeUp 0.5s ease both",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <Link to="/" style={{ display: "inline-block", marginBottom: 24 }}>
            <img
              src="/logo.png"
              alt="KwiKie"
              style={{ height: 40, width: "auto" }}
            />
          </Link>
          <div
            style={{
              fontFamily: "var(--font-head)",
              fontSize: 11,
              fontWeight: 600,
              color: "var(--accent)",
              letterSpacing: "2px",
              textTransform: "uppercase",
              marginBottom: 20,
            }}
          >
            Developer Portal
          </div>
          <h1
            style={{
              fontSize: 26,
              fontWeight: 800,
              letterSpacing: "-1px",
              marginBottom: 8,
            }}
          >
            Sign in to your account
          </h1>
          <p style={{ fontSize: 14, color: "var(--text2)" }}>
            Manage your API keys, webhooks and usage
          </p>
        </div>

        <div
          style={{
            background: "var(--bg2)",
            border: "1px solid var(--border)",
            borderRadius: 16,
            padding: 32,
          }}
        >
          <div className="form-group">
            <label className="form-label">Business Email</label>
            <input
              className="form-input"
              type="email"
              placeholder="your@business.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              className="form-input"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
            />
          </div>
          <button
            className="btn-primary"
            style={{ width: "100%", justifyContent: "center", marginTop: 8 }}
            onClick={submit}
            disabled={loading}
          >
            {loading ? <span className="spinner" /> : "Sign In →"}
          </button>
        </div>

        <p
          style={{
            textAlign: "center",
            fontSize: 13,
            color: "var(--text3)",
            marginTop: 20,
          }}
        >
          Don't have an account?{" "}
          <Link to="/#api" style={{ color: "var(--accent)" }}>
            Get a free API key →
          </Link>
        </p>
        <p
          style={{
            textAlign: "center",
            fontSize: 13,
            color: "var(--text3)",
            marginTop: 8,
          }}
        >
          <Link to="/" style={{ color: "var(--text3)" }}>
            ← Back to home
          </Link>
        </p>
      </div>
    </div>
  );
}

// ── Credential row ────────────────────────────────────────────────────────────
function CredRow({ label, value, hint, sensitive }) {
  const [visible, setVisible] = useState(!sensitive);
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div
      style={{
        background: "var(--bg3)",
        border: "1px solid var(--border)",
        borderRadius: 10,
        padding: "14px 16px",
        marginBottom: 10,
      }}
    >
      <div
        style={{
          fontSize: 10,
          color: "var(--text3)",
          fontFamily: "var(--font-mono)",
          letterSpacing: "0.8px",
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 13,
            color: "var(--accent)",
            flex: 1,
            wordBreak: "break-all",
          }}
        >
          {visible ? value : "•".repeat(Math.min(value?.length || 32, 40))}
        </div>
        {sensitive && (
          <button
            className="btn-ghost btn-sm"
            onClick={() => setVisible((v) => !v)}
            style={{ flexShrink: 0 }}
          >
            {visible ? <EyeOff size={13} /> : <Eye size={13} />}
          </button>
        )}
        <button
          className="btn-ghost btn-sm"
          onClick={copy}
          style={{ flexShrink: 0 }}
        >
          {copied ? (
            "✓"
          ) : (
            <>
              <Copy size={13} /> Copy
            </>
          )}
        </button>
      </div>
      {hint && (
        <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 5 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

// ── Main portal ───────────────────────────────────────────────────────────────
export default function Developer() {
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("credentials"); // credentials | webhook | logs
  const [webhookUrl, setWebhookUrl] = useState("");
  const [savingWh, setSavingWh] = useState(false);
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [apiLogs, setApiLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [regenerating, setRegenerating] = useState(false);

  const apiKey = localStorage.getItem("kq_b2b_key");

  const loadClient = useCallback(
    async (key) => {
      const k = key || apiKey;
      if (!k) {
        setLoading(false);
        return;
      }
      try {
        const r = await b2bAPI.me(k);
        setClient(r.data.data);
        setWebhookUrl(r.data.data.webhook_url || "");
      } catch (e) {
        // Key invalid or expired — clear and show login
        localStorage.removeItem("kq_b2b_key");
        localStorage.removeItem("kq_b2b_secret");
      }
      setLoading(false);
    },
    [apiKey],
  );

  useEffect(() => {
    loadClient();
  }, [loadClient]);

  const loadLogs = async () => {
    if (!apiKey) return;
    setLogsLoading(true);
    try {
      const [wh, al] = await Promise.all([
        b2bAPI.webhookLogs(apiKey),
        b2bAPI.apiLogs(apiKey),
      ]);
      setWebhookLogs(wh.data.data || []);
      setApiLogs(al.data.data || []);
    } catch {}
    setLogsLoading(false);
  };

  useEffect(() => {
    if (tab === "logs") loadLogs();
  }, [tab]);

  const handleRegenerate = async () => {
    if (
      !window.confirm(
        "Regenerate your API key? Your current key will stop working immediately.",
      )
    )
      return;
    setRegenerating(true);
    try {
      const r = await b2bAPI.regenerate(apiKey);
      const newKey = r.data.data.api_key;
      const newSecret = r.data.data.api_secret;
      localStorage.setItem("kq_b2b_key", newKey);
      localStorage.setItem("kq_b2b_secret", newSecret);
      await loadClient(newKey);
      toast.success("✅ API key regenerated. Update your integration.");
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to regenerate");
    }
    setRegenerating(false);
  };

  const handleSaveWebhook = async () => {
    setSavingWh(true);
    try {
      await b2bAPI.updateWebhook(apiKey, webhookUrl);
      toast.success("Webhook URL saved");
      await loadClient();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to save");
    }
    setSavingWh(false);
  };

  const handleDelete = async () => {
    if (deleteConfirm !== "DELETE") {
      toast.error("Type DELETE to confirm");
      return;
    }
    try {
      await b2bAPI.deleteAccount(apiKey);
      localStorage.removeItem("kq_b2b_key");
      localStorage.removeItem("kq_b2b_secret");
      localStorage.removeItem("kq_b2b_email");
      toast.success("Account deleted. Your API key is revoked.");
      navigate("/");
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to delete");
    }
  };

  const logout = () => {
    localStorage.removeItem("kq_b2b_key");
    localStorage.removeItem("kq_b2b_secret");
    setClient(null);
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 12,
          color: "var(--text3)",
        }}
      >
        <span
          className="spinner-white"
          style={{ width: 28, height: 28, borderTopColor: "var(--accent)" }}
        />
        <span>Loading your portal...</span>
      </div>
    );
  }

  if (!client)
    return (
      <DevLogin
        onLogin={(data) => {
          setClient(data);
          setWebhookUrl(data.webhook_url || "");
        }}
      />
    );

  const statusStyle = STATUS_COLOR[client.status] || STATUS_COLOR.pending;
  const TABS = [
    { id: "credentials", label: "🔑 Credentials" },
    { id: "webhook", label: "🔁 Webhook" },
    { id: "logs", label: "📋 Logs" },
  ];

  return (
    <div style={{ minHeight: "100vh", padding: "90px 6% 60px" }}>
      <div style={{ maxWidth: 820, margin: "0 auto" }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            marginBottom: 32,
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          <div>
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                color: "var(--accent)",
                letterSpacing: "1.5px",
                textTransform: "uppercase",
                marginBottom: 6,
              }}
            >
              Developer Portal
            </div>
            <h1
              style={{
                fontSize: 28,
                fontWeight: 800,
                letterSpacing: "-1px",
                marginBottom: 4,
              }}
            >
              {client.name}
            </h1>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <span style={{ fontSize: 13, color: "var(--text2)" }}>
                {client.email}
              </span>
              <span
                style={{
                  background: statusStyle.bg,
                  color: statusStyle.text,
                  borderRadius: 6,
                  padding: "2px 10px",
                  fontSize: 12,
                  fontWeight: 600,
                  textTransform: "capitalize",
                }}
              >
                {client.status}
              </span>
              {client.rate_limit && (
                <span style={{ fontSize: 12, color: "var(--text3)" }}>
                  {client.rate_limit.toLocaleString()} req/month
                </span>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn-ghost btn-sm" onClick={() => loadClient()}>
              <RefreshCw size={13} /> Refresh
            </button>
            <button
              className="btn-ghost btn-sm"
              style={{ color: "var(--text2)" }}
              onClick={logout}
            >
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>

        {/* Status warning */}
        {client.status === "suspended" && (
          <div
            style={{
              background: "rgba(239,68,68,0.08)",
              border: "1px solid rgba(239,68,68,0.25)",
              borderRadius: 10,
              padding: "14px 18px",
              marginBottom: 20,
              fontSize: 13,
              color: "var(--red)",
              display: "flex",
              gap: 10,
              alignItems: "center",
            }}
          >
            ⚠️ Your account is suspended. Contact{" "}
            <a
              href="mailto:support@kwikkie.app"
              style={{ color: "var(--accent)", marginLeft: 4 }}
            >
              support@kwikkie.app
            </a>
          </div>
        )}

        {/* Tab nav */}
        <div
          style={{
            display: "flex",
            background: "var(--bg3)",
            borderRadius: 10,
            padding: 4,
            marginBottom: 28,
            border: "1px solid var(--border)",
            gap: 4,
          }}
        >
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                flex: 1,
                padding: "10px 12px",
                borderRadius: 8,
                border: "none",
                background: tab === t.id ? "var(--bg4)" : "transparent",
                color: tab === t.id ? "var(--text)" : "var(--text3)",
                fontFamily: "var(--font-head)",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
                transition: "all 0.15s",
                boxShadow: tab === t.id ? "0 2px 8px rgba(0,0,0,0.3)" : "none",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Credentials tab ── */}
        {tab === "credentials" && (
          <div style={{ animation: "fadeUp 0.3s ease both" }}>
            <div
              style={{
                background: "var(--bg2)",
                border: "1px solid var(--border)",
                borderRadius: 16,
                padding: 28,
                marginBottom: 20,
              }}
            >
              <div
                style={{
                  fontFamily: "var(--font-head)",
                  fontSize: 17,
                  fontWeight: 700,
                  marginBottom: 18,
                }}
              >
                API Credentials
              </div>
              <CredRow
                label="API KEY"
                value={client.api_key}
                hint="Add to every request: X-API-Key: YOUR_KEY"
              />
              <CredRow
                label="API SECRET"
                value={client.api_secret}
                hint="Used for HMAC webhook signature verification"
                sensitive
              />
              <CredRow
                label="WEBHOOK SECRET"
                value={client.webhook_secret}
                hint="Verify incoming webhooks with this secret"
                sensitive
              />

              {/* Regenerate */}
              <div
                style={{
                  marginTop: 20,
                  paddingTop: 20,
                  borderTop: "1px solid var(--border)",
                }}
              >
                <div
                  style={{
                    fontFamily: "var(--font-head)",
                    fontSize: 14,
                    fontWeight: 700,
                    marginBottom: 6,
                  }}
                >
                  Regenerate Keys
                </div>
                <p
                  style={{
                    fontSize: 13,
                    color: "var(--text2)",
                    marginBottom: 14,
                  }}
                >
                  Generate a new API key and secret. Your current key will stop
                  working immediately — update your integration before doing
                  this.
                </p>
                <button
                  className="btn-ghost"
                  style={{
                    color: "var(--accent)",
                    borderColor: "rgba(245,166,35,0.3)",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                  onClick={handleRegenerate}
                  disabled={regenerating}
                >
                  {regenerating ? (
                    <span className="spinner-white" />
                  ) : (
                    <RotateCcw size={15} />
                  )}
                  Regenerate API Key
                </button>
              </div>
            </div>

            {/* Quick reference */}
            <div
              style={{
                background: "var(--bg2)",
                border: "1px solid var(--border)",
                borderRadius: 16,
                padding: 28,
                marginBottom: 20,
              }}
            >
              <div
                style={{
                  fontFamily: "var(--font-head)",
                  fontSize: 17,
                  fontWeight: 700,
                  marginBottom: 14,
                }}
              >
                Quick Reference
              </div>
              <div
                style={{
                  background: "var(--bg3)",
                  borderRadius: 10,
                  padding: 16,
                  fontFamily: "var(--font-mono)",
                  fontSize: 12,
                  lineHeight: 1.8,
                  color: "#c9d1d9",
                }}
              >
                <div style={{ color: "#6e7681", marginBottom: 8 }}>
                  # Create a delivery
                </div>
                <div>
                  curl -X POST{" "}
                  {process.env.REACT_APP_API_URL ||
                    "http://localhost:3000/api/v1"}
                  /orders \
                </div>
                <div>
                  &nbsp;&nbsp;-H "X-API-Key:{" "}
                  <span style={{ color: "var(--accent)" }}>
                    {client.api_key}
                  </span>
                  " \
                </div>
                <div>&nbsp;&nbsp;-H "Content-Type: application/json" \</div>
                <div>
                  &nbsp;&nbsp;-d
                  '&#123;"pickup_address":"...","dropoff_address":"..."&#125;'
                </div>
              </div>
            </div>

            {/* Danger zone */}
            <div
              style={{
                background: "rgba(239,68,68,0.04)",
                border: "1px solid rgba(239,68,68,0.15)",
                borderRadius: 16,
                padding: 28,
              }}
            >
              <div
                style={{
                  fontFamily: "var(--font-head)",
                  fontSize: 17,
                  fontWeight: 700,
                  marginBottom: 8,
                  color: "var(--red)",
                }}
              >
                ⚠️ Danger Zone
              </div>
              <p
                style={{
                  fontSize: 13,
                  color: "var(--text2)",
                  marginBottom: 14,
                }}
              >
                Deleting your account will permanently revoke your API key. Any
                active integrations using this key will stop working
                immediately. This cannot be undone.
              </p>
              {!showDelete ? (
                <button
                  className="btn-ghost"
                  style={{
                    color: "var(--red)",
                    borderColor: "rgba(239,68,68,0.3)",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                  onClick={() => setShowDelete(true)}
                >
                  <Trash2 size={15} /> Delete Account & Revoke Key
                </button>
              ) : (
                <div
                  style={{
                    background: "rgba(239,68,68,0.07)",
                    borderRadius: 10,
                    padding: 16,
                  }}
                >
                  <p
                    style={{
                      fontSize: 13,
                      color: "var(--red)",
                      marginBottom: 12,
                    }}
                  >
                    Type <strong>DELETE</strong> to confirm permanent deletion:
                  </p>
                  <div style={{ display: "flex", gap: 10 }}>
                    <input
                      className="form-input"
                      placeholder="Type DELETE"
                      value={deleteConfirm}
                      onChange={(e) => setDeleteConfirm(e.target.value)}
                      style={{ borderColor: "rgba(239,68,68,0.4)" }}
                    />
                    <button
                      className="btn-ghost"
                      style={{
                        color: "var(--red)",
                        borderColor: "rgba(239,68,68,0.4)",
                        flexShrink: 0,
                      }}
                      onClick={handleDelete}
                      disabled={deleteConfirm !== "DELETE"}
                    >
                      Confirm Delete
                    </button>
                    <button
                      className="btn-ghost btn-sm"
                      onClick={() => {
                        setShowDelete(false);
                        setDeleteConfirm("");
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Webhook tab ── */}
        {tab === "webhook" && (
          <div style={{ animation: "fadeUp 0.3s ease both" }}>
            <div
              style={{
                background: "var(--bg2)",
                border: "1px solid var(--border)",
                borderRadius: 16,
                padding: 28,
                marginBottom: 20,
              }}
            >
              <div
                style={{
                  fontFamily: "var(--font-head)",
                  fontSize: 17,
                  fontWeight: 700,
                  marginBottom: 6,
                }}
              >
                Webhook Configuration
              </div>
              <p
                style={{
                  fontSize: 13,
                  color: "var(--text2)",
                  marginBottom: 20,
                }}
              >
                KwiKie will POST to your URL at every order status change.
                Verify the signature using your webhook secret.
              </p>
              <div className="form-group">
                <label className="form-label">Webhook URL</label>
                <input
                  className="form-input"
                  placeholder="https://yourbiz.com/webhooks/kwikkie"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                />
              </div>
              <button
                className="btn-primary"
                onClick={handleSaveWebhook}
                disabled={savingWh}
              >
                {savingWh ? <span className="spinner" /> : "Save Webhook URL"}
              </button>
            </div>

            {/* Payload reference */}
            <div
              style={{
                background: "var(--bg2)",
                border: "1px solid var(--border)",
                borderRadius: 16,
                padding: 28,
              }}
            >
              <div
                style={{
                  fontFamily: "var(--font-head)",
                  fontSize: 15,
                  fontWeight: 700,
                  marginBottom: 14,
                }}
              >
                Webhook Payload
              </div>
              <div
                style={{
                  background: "var(--bg3)",
                  borderRadius: 10,
                  padding: 16,
                  fontFamily: "var(--font-mono)",
                  fontSize: 12,
                  lineHeight: 1.8,
                  color: "#c9d1d9",
                }}
              >
                {`{
  "event":      "order_status_updated",
  "order_id":   921,
  "status":     "picked_up",
  "rider_name": "Kofi Mensah",
  "timestamp":  "2025-01-15T14:32:00Z"
}`}
              </div>
              <div style={{ marginTop: 14 }}>
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--text3)",
                    marginBottom: 6,
                  }}
                >
                  Verify the signature in Node.js:
                </div>
                <div
                  style={{
                    background: "var(--bg3)",
                    borderRadius: 10,
                    padding: 16,
                    fontFamily: "var(--font-mono)",
                    fontSize: 12,
                    lineHeight: 1.8,
                    color: "#c9d1d9",
                  }}
                >
                  {`const sig = req.headers['x-kwikkie-signature'];
const expected = crypto
  .createHmac('sha256', WEBHOOK_SECRET)
  .update(JSON.stringify(req.body))
  .digest('hex');
if (sig !== expected) return res.status(401).send('Invalid');`}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Logs tab ── */}
        {tab === "logs" && (
          <div style={{ animation: "fadeUp 0.3s ease both" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: 16,
              }}
            >
              <button
                className="btn-ghost btn-sm"
                onClick={loadLogs}
                disabled={logsLoading}
              >
                <RefreshCw size={13} /> Refresh
              </button>
            </div>

            {/* API Logs */}
            <div
              style={{
                background: "var(--bg2)",
                border: "1px solid var(--border)",
                borderRadius: 16,
                padding: 24,
                marginBottom: 20,
              }}
            >
              <div
                style={{
                  fontFamily: "var(--font-head)",
                  fontSize: 16,
                  fontWeight: 700,
                  marginBottom: 14,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Activity size={16} style={{ color: "var(--accent)" }} /> API
                Request Log
              </div>
              {logsLoading ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "32px 0",
                    color: "var(--text3)",
                  }}
                >
                  <span
                    className="spinner-white"
                    style={{
                      margin: "0 auto",
                      display: "block",
                      width: 20,
                      height: 20,
                    }}
                  />
                </div>
              ) : apiLogs.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "32px 0",
                    color: "var(--text3)",
                    fontSize: 13,
                  }}
                >
                  No API requests yet
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 13,
                    }}
                  >
                    <thead>
                      <tr>
                        {["Method", "Endpoint", "Status", "IP", "Time"].map(
                          (h) => (
                            <th
                              key={h}
                              style={{
                                textAlign: "left",
                                padding: "8px 12px",
                                fontSize: 11,
                                color: "var(--text3)",
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                borderBottom: "1px solid var(--border)",
                              }}
                            >
                              {h}
                            </th>
                          ),
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {apiLogs.map((l, i) => (
                        <tr
                          key={i}
                          style={{ borderBottom: "1px solid var(--border)" }}
                        >
                          <td
                            style={{
                              padding: "9px 12px",
                              fontFamily: "var(--font-mono)",
                              fontSize: 11,
                              color:
                                l.method === "POST"
                                  ? "var(--green)"
                                  : "#60a5fa",
                            }}
                          >
                            {l.method}
                          </td>
                          <td
                            style={{
                              padding: "9px 12px",
                              fontFamily: "var(--font-mono)",
                              fontSize: 11,
                              color: "var(--text2)",
                            }}
                          >
                            {l.endpoint}
                          </td>
                          <td
                            style={{
                              padding: "9px 12px",
                              color:
                                l.status_code < 300
                                  ? "var(--green)"
                                  : "var(--red)",
                              fontWeight: 600,
                            }}
                          >
                            {l.status_code}
                          </td>
                          <td
                            style={{
                              padding: "9px 12px",
                              color: "var(--text3)",
                              fontFamily: "var(--font-mono)",
                              fontSize: 11,
                            }}
                          >
                            {l.ip_address}
                          </td>
                          <td
                            style={{
                              padding: "9px 12px",
                              color: "var(--text3)",
                              fontSize: 11,
                            }}
                          >
                            {l.created_at
                              ? format(new Date(l.created_at), "MMM d, HH:mm")
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Webhook Logs */}
            <div
              style={{
                background: "var(--bg2)",
                border: "1px solid var(--border)",
                borderRadius: 16,
                padding: 24,
              }}
            >
              <div
                style={{
                  fontFamily: "var(--font-head)",
                  fontSize: 16,
                  fontWeight: 700,
                  marginBottom: 14,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Webhook size={16} style={{ color: "var(--accent)" }} /> Webhook
                Delivery Log
              </div>
              {logsLoading ? (
                <div style={{ textAlign: "center", padding: "32px 0" }}>
                  <span
                    className="spinner-white"
                    style={{
                      margin: "0 auto",
                      display: "block",
                      width: 20,
                      height: 20,
                    }}
                  />
                </div>
              ) : webhookLogs.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "32px 0",
                    color: "var(--text3)",
                    fontSize: 13,
                  }}
                >
                  No webhook events yet
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 13,
                    }}
                  >
                    <thead>
                      <tr>
                        {[
                          "Event",
                          "Order",
                          "Response",
                          "Delivered",
                          "Attempts",
                          "Time",
                        ].map((h) => (
                          <th
                            key={h}
                            style={{
                              textAlign: "left",
                              padding: "8px 12px",
                              fontSize: 11,
                              color: "var(--text3)",
                              fontWeight: 600,
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              borderBottom: "1px solid var(--border)",
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {webhookLogs.map((l, i) => (
                        <tr
                          key={i}
                          style={{ borderBottom: "1px solid var(--border)" }}
                        >
                          <td
                            style={{
                              padding: "9px 12px",
                              fontFamily: "var(--font-mono)",
                              fontSize: 11,
                              color: "var(--text2)",
                            }}
                          >
                            {l.event}
                          </td>
                          <td
                            style={{
                              padding: "9px 12px",
                              fontFamily: "var(--font-mono)",
                              fontSize: 11,
                              color: "var(--accent)",
                            }}
                          >
                            #{l.order_id}
                          </td>
                          <td
                            style={{
                              padding: "9px 12px",
                              color:
                                l.response_code < 300
                                  ? "var(--green)"
                                  : "var(--red)",
                              fontWeight: 600,
                            }}
                          >
                            {l.response_code || "—"}
                          </td>
                          <td style={{ padding: "9px 12px" }}>
                            {l.delivered ? "✅" : "❌"}
                          </td>
                          <td
                            style={{
                              padding: "9px 12px",
                              color: "var(--text3)",
                            }}
                          >
                            {l.attempts}
                          </td>
                          <td
                            style={{
                              padding: "9px 12px",
                              color: "var(--text3)",
                              fontSize: 11,
                            }}
                          >
                            {l.created_at
                              ? format(new Date(l.created_at), "MMM d, HH:mm")
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
